import React, { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Sparkles, Heart, Play, Clock, CheckCircle2, Star } from "lucide-react";

// Local YouTube ID extractor (mirrors backend regex)
function ytIdFrom(url) {
  if (!url) return null;
  const m = String(url).match(/(?:youtube\.com\/(?:watch\?[^ ]*v=|shorts\/|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : null;
}

/**
 * "Share your story" section on Insights — lets users submit their own success
 * story (photo + optional YouTube haldi/wedding video). Once submitted, shows
 * status (Pending / Live / Featured). Users can re-submit if all past
 * submissions were rejected/deleted.
 */
export default function ShareYourStorySection() {
  const [mine, setMine] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    couple_names: "",
    story: "",
    city: "",
    wedding_date: "",
    photo_url: "",
    youtube_url: "",
  });

  const load = () => {
    api.get("/success-stories/my")
      .then(({ data }) => setMine(data.stories || []))
      .catch(() => setMine([]));
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (form.couple_names.trim().length < 3) return toast.error("Enter both partners' names");
    if (form.story.trim().length < 20) return toast.error("Story must be at least 20 characters");
    setBusy(true);
    try {
      await api.post("/success-stories/submit", form);
      toast.success("Story sent for admin review · thank you 💛");
      setOpen(false);
      setForm({ couple_names: "", story: "", city: "", wedding_date: "", photo_url: "", youtube_url: "" });
      load();
    } catch (e) {
      toast.error(formatError(e));
    } finally { setBusy(false); }
  };

  const latest = mine[0];
  const yid = ytIdFrom(form.youtube_url);

  return (
    <div
      className="mt-8 relative overflow-hidden border p-6"
      style={{ borderRadius: 14, borderColor: "var(--umg-border)", background: "linear-gradient(135deg, #FFF7ED 0%, #FCE7F3 60%, #EDE9FE 100%)" }}
      data-testid="share-your-story-section"
    >
      <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full opacity-20" style={{ background: "#DB2777" }} />
      <div className="relative flex items-start gap-4 flex-wrap sm:flex-nowrap">
        <div className="w-11 h-11 rounded-xl grid place-items-center shrink-0 bg-white/70 backdrop-blur">
          <Sparkles className="w-5 h-5 text-[#C85A53]" strokeWidth={1.8} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="uppercase tracking-[0.28em] text-[10px] font-semibold text-[#C85A53]">Share your story</div>
          <h3 className="font-display text-2xl mt-1">Found your rishta on Umaang?</h3>
          <p className="mt-2 text-sm text-[#57534E] max-w-xl">
            Apni haldi ya wedding reel share karo — 100+ waiting families ko inspire karo.
            Approved stories landing page ke carousel me feature hoti hain.
          </p>

          {latest ? (
            <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/80 backdrop-blur border" style={{ borderColor: "var(--umg-border)" }}>
              {latest.approved && latest.featured ? (
                <><Star className="w-3.5 h-3.5 text-[#F59E0B]" fill="#F59E0B" /> <span className="text-xs font-semibold">Featured on landing</span></>
              ) : latest.approved ? (
                <><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> <span className="text-xs font-semibold">Live on landing</span></>
              ) : (
                <><Clock className="w-3.5 h-3.5 text-amber-600" /> <span className="text-xs font-semibold">Pending admin review</span></>
              )}
              <span className="text-[10px] text-[color:var(--umg-text-2)]">· {latest.couple_names}</span>
            </div>
          ) : null}
        </div>
        <Button
          data-testid="share-story-open-btn"
          onClick={() => setOpen(true)}
          className="btn-pill bg-gradient-to-r from-[#C85A53] via-[#DB2777] to-[#7C3AED] hover:opacity-90 text-white h-10 px-5 shrink-0"
        >
          <Heart className="w-4 h-4 mr-2" fill="white" />
          {latest ? "Submit another" : "Share your story"}
        </Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Share your rishta story</DialogTitle>
            <DialogDescription>
              Once approved, your story will appear on the Umaang landing carousel — an inspiration for many.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Couple names</Label>
              <Input
                data-testid="my-story-couple"
                className="mt-1"
                value={form.couple_names}
                onChange={(e) => setForm({ ...form, couple_names: e.target.value })}
                placeholder="e.g. Riya & Aryan"
              />
            </div>
            <div>
              <Label>Your story</Label>
              <textarea
                data-testid="my-story-text"
                className="mt-1 w-full border rounded-md p-2 text-sm min-h-[120px]"
                style={{ borderColor: "var(--umg-border)", background: "var(--umg-secondary)" }}
                value={form.story}
                onChange={(e) => setForm({ ...form, story: e.target.value })}
                placeholder="Aap dono ka Umaang par first hello kaisa tha? What made this rishta special? Family reactions?"
                maxLength={2000}
              />
              <div className="text-[10px] text-[color:var(--umg-text-2)] mt-0.5">
                {form.story.length}/2000 characters — write in Hindi, English, or Hinglish
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>City</Label>
                <Input data-testid="my-story-city" className="mt-1" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Delhi" />
              </div>
              <div>
                <Label>Wedding date</Label>
                <Input data-testid="my-story-wedding-date" type="date" className="mt-1" value={form.wedding_date} onChange={(e) => setForm({ ...form, wedding_date: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Photo URL <span className="text-xs text-[color:var(--umg-text-2)]">(optional if video below)</span></Label>
              <Input
                data-testid="my-story-photo-url"
                className="mt-1"
                value={form.photo_url}
                onChange={(e) => setForm({ ...form, photo_url: e.target.value })}
                placeholder="https://…jpg — link to a couple photo"
              />
              {form.photo_url && (
                <img
                  src={form.photo_url}
                  alt=""
                  className="mt-2 h-28 object-cover rounded-lg"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              )}
            </div>
            <div>
              <Label>YouTube URL <span className="text-xs text-[color:var(--umg-text-2)]">(optional — haldi/wedding reel)</span></Label>
              <Input
                data-testid="my-story-youtube"
                className="mt-1"
                value={form.youtube_url}
                onChange={(e) => setForm({ ...form, youtube_url: e.target.value })}
                placeholder="https://youtu.be/…"
              />
              {form.youtube_url && !yid && (
                <div className="text-[11px] text-amber-700 mt-1">
                  Not a valid YouTube URL. Try youtu.be/ or /watch?v= or /shorts/ links.
                </div>
              )}
              {yid && (
                <div className="relative mt-2 h-28 w-48 rounded-lg overflow-hidden bg-black">
                  <img src={`https://img.youtube.com/vi/${yid}/hqdefault.jpg`} alt="Video preview" className="w-full h-full object-cover opacity-90" />
                  <div className="absolute inset-0 grid place-items-center">
                    <div className="w-10 h-10 rounded-full bg-white/95 grid place-items-center">
                      <Play className="w-4 h-4 text-[#C85A53] ml-0.5" fill="#C85A53" />
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="text-[11px] text-[color:var(--umg-text-2)] bg-white/60 border rounded-md p-2" style={{ borderColor: "var(--umg-border)" }}>
              Note: Umaang team reviews every story to keep the landing carousel authentic. Publishing takes 1-2 days after approval.
            </div>
          </div>
          <DialogFooter>
            <Button
              data-testid="my-story-submit"
              onClick={submit}
              disabled={busy}
              className="btn-pill bg-gradient-to-r from-[#C85A53] to-[#DB2777] hover:opacity-90 text-white"
            >
              {busy ? "Submitting…" : "Submit for review"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
